import React, { useEffect, useState } from "react";
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
  LineChart, Line, CartesianGrid, Legend,
} from "recharts";
import api from "@/lib/api";

const NAVY = "#003B73";
const GOLD = "#F9A74A";
const SKY = "#6CCFF6";
const TEAL = "#39AFA3";
const YELLOW = "#FFD43B";
const PIE_COLORS = [NAVY, GOLD, SKY, TEAL, YELLOW, "#00519C", "#9CB4CC"];

const Card = ({ title, subtitle, children, testid }) => (
  <div className="bg-white border border-[var(--brdr)] rounded-sm p-6 shadow-sm" data-testid={testid}>
    <h3 className="font-serif text-xl text-oxford">{title}</h3>
    {subtitle && <p className="text-xs text-[var(--text2)] mt-1 mb-4">{subtitle}</p>}
    <div className={subtitle ? "" : "mt-4"} style={{ width: "100%", height: 280 }}>
      {children}
    </div>
  </div>
);

const tooltipStyle = { borderRadius: 2, border: "1px solid #DDE6EF", fontSize: 12 };

export default function Analytics() {
  const [data, setData] = useState(null);
  const [versions, setVersions] = useState([]);
  const [version, setVersion] = useState("");

  const load = (v) => {
    const params = {};
    if (v) params.workbook_version = v;
    api.get("/admin/analytics", { params }).then((r) => setData(r.data)).catch(() => {});
  };

  useEffect(() => {
    api.get("/admin/workbooks").then((r) => setVersions(r.data)).catch(() => {});
    load("");
  }, []);

  const onVersion = (e) => { setVersion(e.target.value); load(e.target.value); };

  if (!data) return <div className="text-[var(--text2)]">Loading analytics…</div>;

  return (
    <div data-testid="analytics-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="overline mb-2">Insights</div>
          <h1 className="font-serif text-4xl text-oxford tracking-tight">Analytics</h1>
        </div>
        <select data-testid="analytics-version-filter" value={version} onChange={onVersion}
          className="h-10 bg-white border border-[var(--brdr)] rounded-md px-3 text-sm mt-2">
          <option value="">All workbook versions</option>
          {versions.map((w) => <option key={w.version} value={w.version}>v{w.version}{w.active ? " (active)" : ""}</option>)}
        </select>
      </div>

      {/* KPI row */}
      <div className="grid sm:grid-cols-3 gap-6 mt-8">
        {[
          { label: "Completed Assessments", value: data.total, color: NAVY },
          { label: "Avg Business Score", value: data.avg_business?.toFixed(1), color: NAVY },
          { label: "Avg Legacy Score", value: data.avg_viveka?.toFixed(1), color: GOLD },
        ].map((k) => (
          <div key={k.label} className="bg-white border border-[var(--brdr)] rounded-sm p-6 shadow-sm">
            <div className="overline">{k.label}</div>
            <div className="font-serif text-5xl mt-3" style={{ color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {data.total === 0 ? (
        <div className="bg-white border border-[var(--brdr)] rounded-sm p-10 mt-6 text-center text-[var(--text2)]">
          No completed submissions yet for this selection.
        </div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6 mt-6">
          <Card title="Average Scores by Sector" subtitle="Business vs Legacy" testid="chart-by-sector">
            <ResponsiveContainer>
              <BarChart data={data.by_sector} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="sector" tick={{ fontSize: 11, fill: "#4A5568" }} />
                <YAxis domain={[0, 10]} tick={{ fontSize: 11, fill: "#4A5568" }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="avg_business" name="Business" fill={NAVY} radius={[2, 2, 0, 0]} />
                <Bar dataKey="avg_viveka" name="Legacy" fill={GOLD} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Score Distribution" subtitle="Number of respondents per score band" testid="chart-distribution">
            <ResponsiveContainer>
              <BarChart data={data.score_distribution} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="range" tick={{ fontSize: 11, fill: "#4A5568" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#4A5568" }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="business" name="Business" fill={NAVY} radius={[2, 2, 0, 0]} />
                <Bar dataKey="viveka" name="Legacy" fill={GOLD} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Average Score by Component" subtitle="Across all completed assessments" testid="chart-components">
            <ResponsiveContainer>
              <BarChart layout="vertical" data={data.component_averages} margin={{ top: 4, right: 16, left: 40, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" horizontal={false} />
                <XAxis type="number" domain={[0, 10]} tick={{ fontSize: 11, fill: "#4A5568" }} />
                <YAxis type="category" dataKey="name" width={120} tick={{ fontSize: 11, fill: "#4A5568" }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="avg" name="Avg score" fill={TEAL} radius={[0, 2, 2, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>

          <Card title="Respondents by Sector" subtitle="Share of total submissions" testid="chart-sector-pie">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={data.by_sector} dataKey="count" nameKey="sector" cx="50%" cy="50%"
                  outerRadius={95} innerRadius={45} paddingAngle={2}>
                  {data.by_sector.map((e, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </Card>

          {data.over_time.length > 1 && (
            <Card title="Submissions Over Time" subtitle="Completed assessments per month" testid="chart-over-time">
              <ResponsiveContainer>
                <LineChart data={data.over_time} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#4A5568" }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#4A5568" }} />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Line type="monotone" dataKey="count" stroke={NAVY} strokeWidth={2} dot={{ r: 3, fill: GOLD }} />
                </LineChart>
              </ResponsiveContainer>
            </Card>
          )}

          <Card title="Submissions by Workbook Version" subtitle="Distribution across definitions" testid="chart-versions">
            <ResponsiveContainer>
              <BarChart data={data.by_version} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EEF2F7" vertical={false} />
                <XAxis dataKey="version" tick={{ fontSize: 11, fill: "#4A5568" }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: "#4A5568" }} />
                <Tooltip contentStyle={tooltipStyle} />
                <Bar dataKey="count" name="Submissions" fill={SKY} radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </Card>
        </div>
      )}
    </div>
  );
}
